import React, { useState } from "react";
import { client } from "@/lib/api";
import { toast } from "sonner";

// WW-143 · duplicate purge with two modes. "safe" keeps the WW-154 keeper
// rules; "all" (rule D74) deletes every duplicate, past ones included.
// Confirm needs the exact count the preview showed.
const MODES = [
  { k: "safe", lbl: "safe · keeper rules on" },
  { k: "all", lbl: "all duplicates · past ones included (D74)" },
];
const box = { padding: 16, borderRadius: 14, border: "1px solid var(--ww-line)", background: "var(--ww-surface)" };
const btn = (bg, color) => ({ padding: "10px 16px", borderRadius: 10, border: `1px solid ${bg}`, background: bg, color,
  fontFamily: "Poppins", fontWeight: 700, fontSize: 13, textTransform: "lowercase", cursor: "pointer", minHeight: 40 });

export const DuplicatePurgePanel = () => {
  const [mode, setMode] = useState("safe");
  const [preview, setPreview] = useState(null);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async (m = mode) => {
    setBusy(true);
    try { const r = await client.get(`/admin/duplicates/purge-preview?mode=${m}`); setPreview(r.data); setTyped(""); }
    catch { toast.error("preview failed"); } finally { setBusy(false); }
  };
  const purge = async () => {
    setBusy(true);
    try {
      const r = await client.post("/admin/duplicates/purge", { mode, confirm_count: Number(typed) });
      toast.success(`deleted ${r.data.deleted} duplicates`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail?.message || "purge failed"); } finally { setBusy(false); }
  };
  const n = preview?.would_delete_count ?? 0;
  const s = preview?.summary || {};
  const notDup = async (id) => {
    setBusy(true);
    try { await client.post(`/admin/duplicates/${id}/not-duplicate`); toast.success("back in the review queue"); await load(); }
    catch { toast.error("couldn't unhide"); } finally { setBusy(false); }
  };
  return (
    <div data-testid="ops-card-dup-purge" style={box}>
      <h2 style={{ fontFamily: "Poppins", fontWeight: 800, fontSize: 18 }}>WW-143 · duplicate purge</h2>
      <p style={{ fontFamily: "Arial", fontSize: 13, color: "var(--ww-text-muted)", marginTop: 4 }}>
        preview first. type the exact count to delete. run on live only when vania says so.
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {MODES.map((m) => (
          <button key={m.k} data-testid={`dup-purge-mode-${m.k}`} onClick={() => { setMode(m.k); load(m.k); }}
            style={mode === m.k ? btn("#FFD60A", "#1A1A30") : btn("transparent", "var(--ww-text)")}>{m.lbl}</button>
        ))}
        <button data-testid="dup-purge-preview" onClick={() => load()} disabled={busy} style={btn("transparent", "var(--ww-text)")}>preview</button>
      </div>
      {preview && (
        <div className="mt-3" style={{ fontFamily: "Arial", fontSize: 13 }}>
          {s.counts && (
            <p data-testid="dup-purge-counts" className="mb-2">
              pending: <b data-testid="ops-count-to-review">{s.counts.to_review}</b> upcoming waiting ·{" "}
              <b data-testid="ops-count-past-pending">{s.counts.past_pending}</b> past, still pending ·{" "}
              <b data-testid="ops-count-archived">{s.counts.archived}</b> archived ·{" "}
              <b data-testid="ops-count-hidden-duplicates">{s.counts.hidden_duplicates}</b> hidden duplicates ={" "}
              <b data-testid="ops-count-total-pending">{s.counts.total_pending}</b> pending in all
            </p>
          )}
          <p data-testid="dup-purge-summary">
            database <b>{preview.db_name}</b> · mode <b>{preview.mode}</b> · hidden duplicates {s.duplicates_total} ·
            would delete <b data-testid="dup-purge-would-delete">{n}</b> · past kept {s.past_events_kept_count} ·
            year wrong {s.year_wrong_count} · dead keeper {s.parent_rejected_or_dead_count} · no keeper {s.duplicates_without_parent}
          </p>
          <ul data-testid="dup-purge-list" className="mt-2" style={{ maxHeight: 220, overflow: "auto" }}>
            {(preview.would_delete || []).slice(0, 200).map((r, i) => (
              <li key={`${r.id}-${i}`} data-testid={`dup-purge-row-${r.id}`} className="flex flex-wrap items-center gap-2 py-1" style={{ overflowWrap: "anywhere" }}>
                <span style={{ flex: "1 1 200px", minWidth: 0 }}>{(r.start_at || "no date").slice(0, 10)} · {r.title} · keeps {r.kept_original?.title || "no keeper"}</span>
                <button data-testid={`dup-purge-not-dup-${r.id}`} onClick={() => notDup(r.id)} disabled={busy}
                  style={{ ...btn("transparent", "var(--ww-text)"), minHeight: 32, padding: "4px 10px", fontSize: 12 }}>not a duplicate</button>
              </li>
            ))}
          </ul>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <input data-testid="dup-purge-confirm-input" value={typed} onChange={(e) => setTyped(e.target.value.replace(/\D/g, ""))}
              placeholder={`type ${n} to confirm`} style={{ height: 40, padding: "0 10px", borderRadius: 10, border: "1px solid var(--ww-line-strong)", background: "transparent", color: "var(--ww-text)" }} />
            <button data-testid="dup-purge-confirm" onClick={purge} disabled={busy || !n || Number(typed) !== n}
              style={{ ...btn("#D64545", "#FFFFFF"), opacity: busy || !n || Number(typed) !== n ? 0.5 : 1 }}>
              delete {n} duplicates
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
